import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CropFreeIcon from '@mui/icons-material/CropFree';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { getDocument, PDFDocumentProxy, RenderTask } from 'pdfjs-dist/webpack';
import {
  createBottomTrimCropRegions,
  PromptPdfCropRegion,
  sanitizePromptCropRegions,
} from './pdfPages';

type PixelRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type DragMode = 'create' | 'move' | 'resize-nw' | 'resize-ne' | 'resize-sw' | 'resize-se';

type DragState = {
  mode: DragMode;
  startX: number;
  startY: number;
  startRect: PixelRect | null;
};

type Props = {
  open: boolean;
  file: File | null;
  pageNumbers: number[];
  initialRegions?: PromptPdfCropRegion[];
  onClose: () => void;
  onSkip: () => void;
  onSave: (regions: PromptPdfCropRegion[]) => void;
};

const MIN_RECT_SIZE_PX = 16;

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function rectFromPoints(startX: number, startY: number, endX: number, endY: number): PixelRect {
  const left = Math.min(startX, endX);
  const top = Math.min(startY, endY);
  return {
    x: left,
    y: top,
    width: Math.max(startX, endX) - left,
    height: Math.max(startY, endY) - top,
  };
}

function normalizeRect(rect: PixelRect, width: number, height: number, pageNumber: number): PromptPdfCropRegion {
  return {
    pageNumber,
    xPct: rect.x / width,
    yPct: rect.y / height,
    widthPct: rect.width / width,
    heightPct: rect.height / height,
  };
}

function pixelRectFromSelection(selection: PromptPdfCropRegion, width: number, height: number): PixelRect {
  return {
    x: selection.xPct * width,
    y: selection.yPct * height,
    width: selection.widthPct * width,
    height: selection.heightPct * height,
  };
}

function resizeRect(
  rect: PixelRect,
  mode: DragMode,
  nextX: number,
  nextY: number,
  boundsWidth: number,
  boundsHeight: number,
): PixelRect {
  const left = rect.x;
  const top = rect.y;
  const right = rect.x + rect.width;
  const bottom = rect.y + rect.height;

  switch (mode) {
    case 'resize-nw':
      return {
        x: clamp(nextX, 0, right - MIN_RECT_SIZE_PX),
        y: clamp(nextY, 0, bottom - MIN_RECT_SIZE_PX),
        width: right - clamp(nextX, 0, right - MIN_RECT_SIZE_PX),
        height: bottom - clamp(nextY, 0, bottom - MIN_RECT_SIZE_PX),
      };
    case 'resize-ne': {
      const resizedTop = clamp(nextY, 0, bottom - MIN_RECT_SIZE_PX);
      const resizedRight = clamp(nextX, left + MIN_RECT_SIZE_PX, boundsWidth);
      return {
        x: left,
        y: resizedTop,
        width: resizedRight - left,
        height: bottom - resizedTop,
      };
    }
    case 'resize-sw': {
      const resizedLeft = clamp(nextX, 0, right - MIN_RECT_SIZE_PX);
      const resizedBottom = clamp(nextY, top + MIN_RECT_SIZE_PX, boundsHeight);
      return {
        x: resizedLeft,
        y: top,
        width: right - resizedLeft,
        height: resizedBottom - top,
      };
    }
    case 'resize-se': {
      const resizedRight = clamp(nextX, left + MIN_RECT_SIZE_PX, boundsWidth);
      const resizedBottom = clamp(nextY, top + MIN_RECT_SIZE_PX, boundsHeight);
      return {
        x: left,
        y: top,
        width: resizedRight - left,
        height: resizedBottom - top,
      };
    }
    default:
      return rect;
  }
}

function regionsFromMap(regionsByPage: Record<number, PromptPdfCropRegion | null>): PromptPdfCropRegion[] {
  return Object.values(regionsByPage)
    .filter((region): region is PromptPdfCropRegion => !!region)
    .sort((left, right) => left.pageNumber - right.pageNumber);
}

const PromptPdfCropDialog: React.FC<Props> = ({
  open,
  file,
  pageNumbers,
  initialRegions,
  onClose,
  onSkip,
  onSave,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [visiblePages, setVisiblePages] = useState<number[]>([1]);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [regionsByPage, setRegionsByPage] = useState<Record<number, PromptPdfCropRegion | null>>({});
  const [dragState, setDragState] = useState<DragState | null>(null);

  const currentSelection = regionsByPage[currentPageNumber] || null;
  const currentRect = useMemo(() => {
    if (!currentSelection || pageSize.width <= 0 || pageSize.height <= 0) {
      return null;
    }
    return pixelRectFromSelection(currentSelection, pageSize.width, pageSize.height);
  }, [currentSelection, pageSize.height, pageSize.width]);

  const currentPageIndex = visiblePages.indexOf(currentPageNumber);
  const croppedPagesCount = regionsFromMap(regionsByPage).length;

  const updateCurrentPageSelection = useCallback((rect: PixelRect | null) => {
    setRegionsByPage((prev) => {
      const next = { ...prev };
      if (!rect || pageSize.width <= 0 || pageSize.height <= 0) {
        next[currentPageNumber] = null;
        return next;
      }
      next[currentPageNumber] = normalizeRect(rect, pageSize.width, pageSize.height, currentPageNumber);
      return next;
    });
  }, [currentPageNumber, pageSize.height, pageSize.width]);

  useEffect(() => {
    if (!open || !file) {
      setPdfDocument(null);
      setVisiblePages([1]);
      setCurrentPageNumber(1);
      setZoom(1);
      setError(null);
      setLoading(false);
      setRendering(false);
      setPageSize({ width: 0, height: 0 });
      setRegionsByPage({});
      setDragState(null);
      return;
    }

    let cancelled = false;
    let loadingTask: ReturnType<typeof getDocument> | null = null;

    const load = async () => {
      setLoading(true);
      setError(null);
      setZoom(1);
      try {
        const fileBytes = new Uint8Array(await file.arrayBuffer());
        loadingTask = getDocument({ data: fileBytes });
        const nextPdf = await loadingTask.promise;
        if (cancelled) {
          await loadingTask.destroy();
          return;
        }

        const allowed = pageNumbers.filter((page) => page >= 1 && page <= nextPdf.numPages);
        const pages = allowed.length ? allowed : [1];
        const seededList = sanitizePromptCropRegions(initialRegions, pages);
        const seeded: Record<number, PromptPdfCropRegion | null> = {};
        for (const region of (seededList.length ? seededList : createBottomTrimCropRegions(pages))) {
          seeded[region.pageNumber] = region;
        }

        setPdfDocument(nextPdf);
        setVisiblePages(pages);
        setRegionsByPage(seeded);
        setCurrentPageNumber(seededList[0]?.pageNumber || pages[0]);
      } catch (loadError: any) {
        if (!cancelled) {
          setError(loadError?.message || 'Kunde inte läsa PDF-filen.');
          setPdfDocument(null);
          setVisiblePages([1]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
      loadingTask?.destroy().catch(() => undefined);
    };
  }, [file, initialRegions, open, pageNumbers]);

  useEffect(() => {
    if (!open || !pdfDocument || !canvasRef.current) return undefined;

    let cancelled = false;

    const renderPage = async () => {
      setRendering(true);
      setError(null);
      try {
        const page = await pdfDocument.getPage(currentPageNumber);
        if (cancelled || !canvasRef.current) return;

        const baseViewport = page.getViewport({ scale: 1, rotation: 0 });
        const fitScale = Math.max(0.75, Math.min(2.25, 920 / baseViewport.width));
        const viewport = page.getViewport({ scale: fitScale * zoom, rotation: 0 });
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('Canvas-kontekst saknas.');
        }

        renderTaskRef.current?.cancel();
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        setPageSize({ width: viewport.width, height: viewport.height });

        const renderTask = page.render({
          canvasContext: context,
          viewport,
          canvas,
        });
        renderTaskRef.current = renderTask;
        await renderTask.promise;
      } catch (renderError: any) {
        if (!cancelled && renderError?.name !== 'RenderingCancelledException') {
          setError(renderError?.message || 'Kunde inte rendera PDF-sidan.');
        }
      } finally {
        if (!cancelled) {
          setRendering(false);
        }
      }
    };

    void renderPage();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
    };
  }, [currentPageNumber, open, pdfDocument, zoom]);

  const getRelativePoint = useCallback((clientX: number, clientY: number) => {
    const overlay = overlayRef.current;
    if (!overlay) return null;
    const rect = overlay.getBoundingClientRect();
    return {
      x: clamp(clientX - rect.left, 0, pageSize.width),
      y: clamp(clientY - rect.top, 0, pageSize.height),
    };
  }, [pageSize.height, pageSize.width]);

  useEffect(() => {
    if (!dragState) return undefined;

    const handleMouseMove = (event: MouseEvent) => {
      const point = getRelativePoint(event.clientX, event.clientY);
      if (!point) return;

      if (dragState.mode === 'create') {
        const created = rectFromPoints(dragState.startX, dragState.startY, point.x, point.y);
        updateCurrentPageSelection(created.width >= MIN_RECT_SIZE_PX && created.height >= MIN_RECT_SIZE_PX ? created : null);
        return;
      }

      if (!dragState.startRect) return;

      if (dragState.mode === 'move') {
        updateCurrentPageSelection({
          ...dragState.startRect,
          x: clamp(dragState.startRect.x + (point.x - dragState.startX), 0, Math.max(0, pageSize.width - dragState.startRect.width)),
          y: clamp(dragState.startRect.y + (point.y - dragState.startY), 0, Math.max(0, pageSize.height - dragState.startRect.height)),
        });
        return;
      }

      const resized = resizeRect(dragState.startRect, dragState.mode, point.x, point.y, pageSize.width, pageSize.height);
      updateCurrentPageSelection(resized.width >= MIN_RECT_SIZE_PX && resized.height >= MIN_RECT_SIZE_PX ? resized : null);
    };

    const handleMouseUp = () => {
      setDragState(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, getRelativePoint, pageSize.height, pageSize.width, updateCurrentPageSelection]);

  const handleCreateSelection = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const point = getRelativePoint(event.clientX, event.clientY);
    if (!point) return;
    setDragState({
      mode: 'create',
      startX: point.x,
      startY: point.y,
      startRect: null,
    });
    updateCurrentPageSelection(null);
  }, [getRelativePoint, updateCurrentPageSelection]);

  const handleMoveSelection = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!currentRect) return;
    event.stopPropagation();
    const point = getRelativePoint(event.clientX, event.clientY);
    if (!point) return;
    setDragState({
      mode: 'move',
      startX: point.x,
      startY: point.y,
      startRect: currentRect,
    });
  }, [currentRect, getRelativePoint]);

  const handleResizeSelection = useCallback((mode: DragMode) => (event: React.MouseEvent<HTMLDivElement>) => {
    if (!currentRect) return;
    event.stopPropagation();
    const point = getRelativePoint(event.clientX, event.clientY);
    if (!point) return;
    setDragState({
      mode,
      startX: point.x,
      startY: point.y,
      startRect: currentRect,
    });
  }, [currentRect, getRelativePoint]);

  const goToVisiblePage = (delta: number) => {
    const nextIndex = currentPageIndex + delta;
    if (nextIndex < 0 || nextIndex >= visiblePages.length) return;
    setCurrentPageNumber(visiblePages[nextIndex]);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 700 }}>Beskär PDF innan AI läser</Typography>
          <Typography variant="body2" color="text.secondary">
            {file?.name || 'Ingen fil vald'}
          </Typography>
        </Box>
        <Chip
          icon={<CropFreeIcon />}
          label={croppedPagesCount > 0 ? `${croppedPagesCount} sida/sidor beskurna` : 'Ingen beskärning'}
          color={croppedPagesCount > 0 ? 'success' : 'default'}
          variant={croppedPagesCount > 0 ? 'filled' : 'outlined'}
        />
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, md: 3 }, py: 2.5 }}>
        <Stack spacing={2}>
          <Alert severity="info">
            Markera etikettens innehåll och lämna tryckinformationen längst ner utanför rutan.
            Ett förslag som klipper bort den nedre listen är redan valt. Originalfilen ändras inte.
          </Alert>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                startIcon={<ChevronLeftIcon />}
                disabled={currentPageIndex <= 0}
                onClick={() => goToVisiblePage(-1)}
              >
                Föregående sida
              </Button>
              <Chip
                label={`Sida ${currentPageNumber}${visiblePages.length > 1 ? ` · ${currentPageIndex + 1}/${visiblePages.length}` : ''}`}
                variant="outlined"
              />
              <Button
                size="small"
                variant="outlined"
                endIcon={<ChevronRightIcon />}
                disabled={currentPageIndex < 0 || currentPageIndex >= visiblePages.length - 1}
                onClick={() => goToVisiblePage(1)}
              >
                Nästa sida
              </Button>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
              <Typography sx={{ minWidth: 36, fontSize: '0.85rem', color: 'text.secondary' }}>Zoom</Typography>
              <Slider
                min={0.7}
                max={2.5}
                step={0.05}
                value={zoom}
                onChange={(_event, value) => setZoom(Array.isArray(value) ? value[0] : value)}
                sx={{ maxWidth: 260 }}
              />
              <Chip label={`${Math.round(zoom * 100)} %`} size="small" variant="outlined" />
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCutIcon />}
                onClick={() => {
                  const next: Record<number, PromptPdfCropRegion | null> = {};
                  for (const region of createBottomTrimCropRegions(visiblePages)) {
                    next[region.pageNumber] = region;
                  }
                  setRegionsByPage(next);
                }}
              >
                Klipp bort tryckinfo
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => updateCurrentPageSelection(null)}
                disabled={!currentSelection}
              >
                Rensa sida
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={() => setRegionsByPage({})}
                disabled={croppedPagesCount === 0}
              >
                Rensa alla
              </Button>
            </Stack>
          </Stack>

          <Box
            sx={{
              minHeight: 520,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: '#F7F7F8',
              overflow: 'auto',
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: loading ? 'center' : 'flex-start',
            }}
          >
            {loading ? (
              <Stack spacing={1.5} alignItems="center">
                <CircularProgress size={30} />
                <Typography variant="body2" color="text.secondary">
                  Läser PDF-filen...
                </Typography>
              </Stack>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <Box sx={{ position: 'relative', width: `${pageSize.width}px`, height: `${pageSize.height}px` }}>
                <canvas ref={canvasRef} style={{ display: 'block', borderRadius: 8, boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)' }} />
                <Box
                  ref={overlayRef}
                  onMouseDown={handleCreateSelection}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: `${pageSize.width}px`,
                    height: `${pageSize.height}px`,
                    cursor: rendering ? 'progress' : 'crosshair',
                  }}
                >
                  {rendering && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(255, 255, 255, 0.45)',
                        zIndex: 5,
                      }}
                    >
                      <CircularProgress size={24} />
                    </Box>
                  )}

                  {currentRect && (
                    <Box
                      onMouseDown={handleMoveSelection}
                      sx={{
                        position: 'absolute',
                        left: `${currentRect.x}px`,
                        top: `${currentRect.y}px`,
                        width: `${currentRect.width}px`,
                        height: `${currentRect.height}px`,
                        border: '2px solid #1976d2',
                        bgcolor: 'rgba(25, 118, 210, 0.12)',
                        boxShadow: '0 0 0 9999px rgba(17, 24, 39, 0.26)',
                        cursor: 'move',
                      }}
                    >
                      {([
                        { key: 'resize-nw', left: -6, top: -6, cursor: 'nwse-resize' },
                        { key: 'resize-ne', right: -6, top: -6, cursor: 'nesw-resize' },
                        { key: 'resize-sw', left: -6, bottom: -6, cursor: 'nesw-resize' },
                        { key: 'resize-se', right: -6, bottom: -6, cursor: 'nwse-resize' },
                      ] as const).map((handle) => (
                        <Box
                          key={handle.key}
                          onMouseDown={handleResizeSelection(handle.key)}
                          sx={{
                            position: 'absolute',
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: '#1976d2',
                            border: '2px solid #fff',
                            cursor: handle.cursor,
                            ...(('left' in handle && handle.left !== undefined) ? { left: handle.left } : {}),
                            ...(('right' in handle && handle.right !== undefined) ? { right: handle.right } : {}),
                            ...(('top' in handle && handle.top !== undefined) ? { top: handle.top } : {}),
                            ...(('bottom' in handle && handle.bottom !== undefined) ? { bottom: handle.bottom } : {}),
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Stäng utan att spara</Button>
        <Button onClick={onSkip}>Använd hela sidan</Button>
        <Button variant="contained" onClick={() => onSave(regionsFromMap(regionsByPage))} sx={{ bgcolor: '#1B3967' }}>
          Spara beskärning
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromptPdfCropDialog;
